(() => {
  const MANAGER_KEY = '__cmVariantSpecSectionManager';

  if (window[MANAGER_KEY]) {
    window[MANAGER_KEY].scan();
    return;
  }

  const state = {
    roots: new Set(),
    controllers: new WeakMap(),
    requestIds: new WeakMap(),
    unsubscribe: null,
  };

  const getSubscribeContext = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.variantChange) {
      return {
        subscribe,
        eventName: PUB_SUB_EVENTS.variantChange,
      };
    }

    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
      return {
        subscribe: window.subscribe,
        eventName: window.PUB_SUB_EVENTS.variantChange,
      };
    }

    if (window.theme?.pubsub?.subscribe && window.theme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
      return {
        subscribe: window.theme.pubsub.subscribe.bind(window.theme.pubsub),
        eventName: window.theme.pubsub.PUB_SUB_EVENTS.variantChange,
      };
    }

    return null;
  };

  const syncRoot = (currentRoot, nextRoot) => {
    if (!currentRoot || !nextRoot) return;

    currentRoot.className = nextRoot.className;
    currentRoot.setAttribute('data-has-content', nextRoot.getAttribute('data-has-content') || 'false');
    currentRoot.innerHTML = nextRoot.innerHTML;
  };

  const renderForVariant = async (root, variantId) => {
    const sectionId = root.getAttribute('data-section-id');
    const productUrl = root.getAttribute('data-product-url');

    if (!sectionId || !productUrl) return;

    const requestId = (state.requestIds.get(root) || 0) + 1;
    state.requestIds.set(root, requestId);

    const prevController = state.controllers.get(root);
    if (prevController) prevController.abort();

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    if (controller) state.controllers.set(root, controller);

    const url = new URL(productUrl, window.location.origin);
    if (variantId) {
      url.searchParams.set('variant', String(variantId));
    } else {
      url.searchParams.delete('variant');
    }
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), {
      signal: controller?.signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      console.warn('[cm-variant-spec-section] Failed to fetch section HTML.', {
        status: response.status,
        statusText: response.statusText,
        url: url.toString(),
      });
      return;
    }

    const html = await response.text();
    if (state.requestIds.get(root) !== requestId) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextRoot = parsed.querySelector(`[data-cm-variant-spec-section][data-section-id="${sectionId}"]`) ||
      parsed.querySelector('[data-cm-variant-spec-section]');

    if (!nextRoot) {
      console.warn('[cm-variant-spec-section] Section root was not found in fetched HTML.', {
        sectionId,
        url: url.toString(),
      });
      return;
    }

    syncRoot(root, nextRoot);
  };

  const onVariantChange = (event) => {
    const variant = event?.data?.variant || event?.variant || null;
    const variantId = variant?.id || null;

    state.roots.forEach((root) => {
      if (!root.isConnected) {
        state.roots.delete(root);
        return;
      }

      renderForVariant(root, variantId).catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error('[cm-variant-spec-section] Failed to update section.', error);
      });
    });
  };

  const ensureSubscribed = () => {
    if (state.unsubscribe) return;

    const context = getSubscribeContext();
    if (!context) return;

    state.unsubscribe = context.subscribe(context.eventName, onVariantChange);
  };

  const scan = (target = document) => {
    if (!target?.querySelectorAll) return;

    target.querySelectorAll('[data-cm-variant-spec-section]').forEach((root) => {
      state.roots.add(root);
    });

    ensureSubscribed();
  };

  window[MANAGER_KEY] = {
    scan,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    scan(event.target || document);
  });
})();
